// ─────────────────────────────────────────────────────────────────────────────
// Historique des augmentations — fonction pure, dérivée de la frise salariale
// (buildSalaryTimeline). Pour chaque année écoulée (passé → courant), calcule
// le delta annuel vs l'année précédente et qualifie sa cause : montée
// d'échelon vs augmentation de grille (aug_grille_pct).
//
// Base des montants : salaire RÉEL. Les années passées viennent de l'historique
// salarial réellement versé (salary_history, via les `pastPoints` de la frise).
// L'année courante peut être remplacée par la rémunération réelle affichée au
// profil (grille + heures spéciales + primes) via `opts.currentAnnual`, pour que
// la carte concorde avec l'en-tête « Rémunération & échelon ».
// ─────────────────────────────────────────────────────────────────────────────

import type { SalaryPoint } from './progression'

export type RaiseRow = {
  year: number
  echelon: number
  // L'échelon de l'année est connu (échelon grille > 0). Faux pour le passé réel
  // (salary_history ne stocke pas l'échelon) ⇒ la carte masque alors « échelon N ».
  echelonKnown: boolean
  annual: number
  previousAnnual: number
  deltaAnnual: number
  deltaPct: number // ex. 0.034 = +3,4 %
  // 'echelon' = une montée d'échelon a pris effet cette année (peut inclure
  // l'effet grille) ; 'grille' = seule l'augmentation annuelle de la grille.
  cause: 'echelon' | 'grille'
  isHire: boolean
  isCurrent: boolean
}

// Lignes année par année (plus récente d'abord), uniquement passé + courant —
// les projections ne sont pas des augmentations acquises. La première année
// affichable (embauche ou début de frise) n'a pas de précédent ⇒ exclue.
export function buildRaiseHistory(
  points: SalaryPoint[],
  opts?: {
    // Remplace l'annuel du point COURANT par la rémunération réelle affichée au
    // profil (grille + heures spéciales + primes). null/undefined ⇒ on garde
    // l'annuel grille de la frise.
    currentAnnual?: number | null
  },
): RaiseRow[] {
  const eligible = points
    .filter((p) => p.kind !== 'projection' && p.annual !== null)
    .sort((a, b) => a.year - b.year)

  // Annuel effectif d'un point : override de l'année courante si fourni.
  const annualOf = (p: SalaryPoint): number | null =>
    p.kind === 'current' && opts?.currentAnnual != null ? opts.currentAnnual : p.annual

  const rows: RaiseRow[] = []
  for (let i = 1; i < eligible.length; i++) {
    const prev = eligible[i - 1]
    const cur = eligible[i]
    const prevAnnual = annualOf(prev)
    const curAnnual = annualOf(cur)
    if (prevAnnual === null || curAnnual === null) continue
    const delta = curAnnual - prevAnnual
    rows.push({
      year: cur.year,
      echelon: cur.echelon,
      echelonKnown: cur.echelon > 0,
      annual: curAnnual,
      previousAnnual: prevAnnual,
      deltaAnnual: delta,
      deltaPct: prevAnnual > 0 ? delta / prevAnnual : 0,
      // Montée d'échelon seulement si l'échelon précédent est CONNU (> 0) : le
      // passé réel a un échelon inconnu (0) et ne doit pas simuler une montée.
      cause: prev.echelon > 0 && cur.echelon > prev.echelon ? 'echelon' : 'grille',
      isHire: cur.isHire,
      isCurrent: cur.kind === 'current',
    })
  }
  return rows.reverse()
}
